/**
 * ============================================================================
 * NOTES.JS v31.2 - SIMPLE FAB + BUTTON STRATEGY
 * ============================================================================
 *
 * ✅ v31.2: FAB VEREINFACHT
 *           - Draggable FAB entfernt (zu aggressiv auf Touch)
 *           - FAB ist jetzt einfach fixiert: right:16px, bottom:80px
 *           - FAB + Modals AUSSERHALB von .notes-container (overflow:hidden Fix)
 *           - Kein localStorage, kein Touch-Handler, kein Snap-to-Edge
 *
 * ✅ v30.0: INFO MODAL + LAYOUT
 * ✅ v29.0: NEUES LAYOUT
 * ✅ v28.0: PLANIVIO BRAND REDESIGN
 *
 * @version 31.0.0 - FAB Draggable Fix + Button Strategy
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToken } from '../../context/TokenContext';
import { useTranslation } from '../../context/I18nContext';
import { useTheme } from '../../context/ThemeContext';
import { useHousehold } from '../../context/HouseholdContext';
import { 
  LoadingIndicator, 
  Skeleton, 
  SkeletonGrid,
  EmptyState 
} from '../common/Loading';
import NotesOverview from './NotesOverview';
import CreateNoteModal from './CreateNoteModal';
import MentalLoadPicker from './MentalLoadPicker';
import './Notes.css';

// ============================================================================
// API CONFIG + ANALYTICS
// ============================================================================
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const LOADING_DELAY_MS = 200;

console.log('📝 Notes.js v31.2 loaded - SIMPLE FAB + BUTTON STRATEGY 🎯');

const trackAnalyticsEvent = async (token, eventName, metadata = {}) => {
  if (!token || !eventName) return;
  try {
    const event = {
      type: 'event',
      event_name: String(eventName).substring(0, 100),
      component: String(metadata.component || 'Notes').substring(0, 100),
      metadata: metadata,
      timestamp: new Date().toISOString()
    };
    const response = await fetch(`${API_BASE_URL}/api/analytics/track/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ events: [event] })
    });
    if (response.ok) {
      console.log(`✅ Analytics tracked: ${eventName}`, metadata);
    } else if (response.status === 401) {
      console.log(`⚠️ Analytics skipped (401): ${eventName}`);
    }
  } catch (err) {
    console.log(`⚠️ Analytics skipped: ${eventName}`);
  }
};

// ============================================================================
// GLOBAL STATE + SHARED PROMISE (Anti-Flicker)
// ============================================================================
let GLOBAL_NOTES_CACHE = [];
let GLOBAL_HAS_LOADED = false;
let GLOBAL_LOADING_PROMISE = null;

// ============================================================================
// COMPONENT
// ============================================================================
const Notes = ({ active }) => {
  
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { token, loading: tokenLoading } = useToken();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { members } = useHousehold();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const memberOptions = useMemo(() => {
    const options = [{ id: 'alle', value: 'alle', name: 'Alle', avatar: '👥' }];
    if (members && members.length > 0) {
      members.forEach(member => {
        options.push({
          id: String(member.id),
          value: String(member.id),
          name: member.display_name || member.name || `Mitglied ${member.id}`,
          avatar: member.avatar || '👤'
        });
      });
    }
    return options;
  }, [members]);
  
  // ============================================================================
  // STATE
  // ============================================================================
  const [notes, setNotes] = useState(() => GLOBAL_NOTES_CACHE);
  const [isLoading, setIsLoading] = useState(!GLOBAL_HAS_LOADED);
  const [showLoading, setShowLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(!GLOBAL_HAS_LOADED);
  const [error, setError] = useState(null);
  
  const [viewMode, setViewMode] = useState('grid');
  
  const [showModal, setShowModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [showMentalLoadPicker, setShowMentalLoadPicker] = useState(false);

  const [showInfo, setShowInfo] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  // ============================================================================
  // REFS
  // ============================================================================
  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isInitialLoadRef = useRef(!GLOBAL_HAS_LOADED);
  const hasTrackedViewRef = useRef(false);
  const searchTimeoutRef = useRef(null);

  // ============================================================================
  // RESPONSIVE
  // ============================================================================
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // FAB Event
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.module === 'notes') {
        setEditingNote(null);
        setShowModal(true);
      }
    };
    window.addEventListener('planivio-create-new', handler);
    return () => window.removeEventListener('planivio-create-new', handler);
  }, []);

  // ============================================================================
  // Mount/Unmount
  // ============================================================================
  useEffect(() => {
    isMountedRef.current = true;
    console.log('🔄 Notes MOUNTED');
    return () => {
      isMountedRef.current = false;
      console.log('🔄 Notes UNMOUNTING');
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (token && !hasTrackedViewRef.current && active) {
      hasTrackedViewRef.current = true;
      trackAnalyticsEvent(token, 'component_view', { 
        component: 'Notes',
        notesCount: GLOBAL_NOTES_CACHE.length
      });
    }
  }, [token, active]);

  // ============================================================================
  // ANALYTICS HELPER
  // ============================================================================
  const trackEvent = useCallback(async (eventName, metadata = {}) => {
    if (!token) return;
    trackAnalyticsEvent(token, eventName, { component: 'Notes', ...metadata });
  }, [token]);

  // ============================================================================
  // Sync to Global Cache
  // ============================================================================
  useEffect(() => {
    if (notes.length > 0) {
      GLOBAL_NOTES_CACHE = notes;
    }
  }, [notes]);

  // ============================================================================
  // FETCH FUNCTION
  // ============================================================================
  const fetchNotes = useCallback(async () => {
    console.log('📡 Fetching notes from backend...');
    if (!token) throw new Error('Kein Token verfügbar');
    
    const response = await fetch(`${API_BASE_URL}/api/notes/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) throw new Error('Fehler beim Laden der Notizen');
    
    const data = await response.json();
    const notesData = data.notes || [];
    console.log('✅ Fetched', notesData.length, 'notes');
    return notesData;
  }, [token]);

  // ============================================================================
  // LOAD DATA
  // ============================================================================
  const loadData = useCallback(async (forceRefresh = false) => {
    if (!token) {
      console.log('⏳ Waiting for token from TokenContext...');
      return;
    }
    
    if (GLOBAL_HAS_LOADED && GLOBAL_NOTES_CACHE.length > 0 && !forceRefresh) {
      console.log('📦 Using cached notes:', GLOBAL_NOTES_CACHE.length);
      if (isMountedRef.current) {
        setNotes([...GLOBAL_NOTES_CACHE]);
        setIsLoading(false);
        setIsInitialLoad(false);
        isInitialLoadRef.current = false;
      }
      return;
    }
    
    if (GLOBAL_HAS_LOADED && !forceRefresh) {
      if (isMountedRef.current) {
        setNotes([...GLOBAL_NOTES_CACHE]);
        setIsLoading(false);
        setIsInitialLoad(false);
        isInitialLoadRef.current = false;
      }
      return;
    }
    
    if (GLOBAL_LOADING_PROMISE) {
      try {
        const result = await GLOBAL_LOADING_PROMISE;
        if (isMountedRef.current) {
          setNotes([...result]);
          setIsLoading(false);
          setIsInitialLoad(false);
          isInitialLoadRef.current = false;
        }
      } catch (err) {
        if (isMountedRef.current) {
          setError(err.message);
          setIsLoading(false);
          setIsInitialLoad(false);
          isInitialLoadRef.current = false;
        }
      }
      return;
    }
    
    if (isMountedRef.current) {
      setIsLoading(true);
      setError(null);
      if (!isInitialLoadRef.current) {
        loadingTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) setShowLoading(true);
        }, LOADING_DELAY_MS);
      }
    }
    
    GLOBAL_LOADING_PROMISE = fetchNotes();
    
    try {
      const result = await GLOBAL_LOADING_PROMISE;
      GLOBAL_NOTES_CACHE = result;
      GLOBAL_HAS_LOADED = true;
      if (isMountedRef.current) {
        setNotes([...result]);
        setIsInitialLoad(false);
        isInitialLoadRef.current = false;
      }
    } catch (err) {
      console.error('❌ Load error:', err.message);
      trackEvent('notes_load_error', { error: err.message });
      if (isMountedRef.current) {
        setError(err.message);
        setIsInitialLoad(false);
        isInitialLoadRef.current = false;
        if (GLOBAL_NOTES_CACHE.length > 0) setNotes([...GLOBAL_NOTES_CACHE]);
      }
    } finally {
      GLOBAL_LOADING_PROMISE = null;
      if (isMountedRef.current) {
        setIsLoading(false);
        setShowLoading(false);
      }
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }
  }, [fetchNotes, trackEvent, token]);

  // ============================================================================
  // INITIAL LOAD
  // ============================================================================
  useEffect(() => {
    if (!active) return;
    
    if (GLOBAL_HAS_LOADED) {
      setNotes([...GLOBAL_NOTES_CACHE]);
      setIsLoading(false);
      setIsInitialLoad(false);
      isInitialLoadRef.current = false;
      return;
    }
    
    if (isAuthLoading || tokenLoading) return;
    if (!isAuthenticated) {
      setIsLoading(false);
      setIsInitialLoad(false);
      isInitialLoadRef.current = false;
      return;
    }
    if (!token) return;
    
    loadData();
  }, [active, isAuthenticated, isAuthLoading, tokenLoading, token, loadData]);

  // ============================================================================
  // REFRESH HANDLERS
  // ============================================================================
  const handleRetry = useCallback(() => {
    trackEvent('notes_retry', { type: 'manual' });
    GLOBAL_HAS_LOADED = false;
    GLOBAL_LOADING_PROMISE = null;
    setError(null);
    setIsInitialLoad(true);
    isInitialLoadRef.current = true;
    loadData(true);
  }, [loadData, trackEvent]);

  const handleSilentRefresh = useCallback(() => {
    trackEvent('notes_refresh', { type: 'silent' });
    GLOBAL_HAS_LOADED = false;
    GLOBAL_LOADING_PROMISE = null;
    loadData(true);
  }, [loadData, trackEvent]);

  // ============================================================================
  // MODAL HANDLERS
  // ============================================================================
  const handleOpenNewNote = useCallback(() => {
    console.log('📝 Opening new note modal');
    trackEvent('notes_modal_open', { mode: 'create' });
    setEditingNote(null);
    setShowModal(true);
  }, [trackEvent]);

  const handleEditNote = useCallback((note) => {
    console.log('📝 Opening edit note modal:', note.id);
    trackEvent('notes_modal_open', { mode: 'edit', category: note.category });
    setEditingNote(note);
    setShowModal(true);
  }, [trackEvent]);
  
  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  // ============================================================================
  // ✅ v31.2: FAB DRAG ENTFERNT
  // Draggable war zu aggressiv auf Touch — jeder Tap wurde als Drag erkannt.
  // FAB ist jetzt einfach fixiert unten rechts (CSS: right:16px, bottom:80px).
  // Draggable kann später als opt-in Feature hinzugefügt werden.
  // ============================================================================

  // ============================================================================
  // MENTAL LOAD PICKER HANDLERS
  // ============================================================================
  const handleOpenMentalLoadPicker = useCallback(() => {
    trackEvent('mental_load_picker_open');
    setShowMentalLoadPicker(true);
  }, [trackEvent]);

  const handleCloseMentalLoadPicker = useCallback(() => {
    setShowMentalLoadPicker(false);
  }, []);

  const handleAddMentalLoadTasks = useCallback(async (tasks) => {
    console.log('🧠 Adding Mental Load tasks as notes:', tasks.length);
    if (!token || tasks.length === 0) return;

    trackEvent('mental_load_tasks_add', { count: tasks.length });

    for (const task of tasks) {
      const noteData = {
        title: `${task.emoji} ${task.name}`,
        content: task.description || '',
        category: 'important',
        color: 'pink'
      };

      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const optimisticNote = {
        ...noteData,
        id: tempId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      GLOBAL_NOTES_CACHE = [optimisticNote, ...GLOBAL_NOTES_CACHE];
      GLOBAL_HAS_LOADED = true;
      setNotes([...GLOBAL_NOTES_CACHE]);

      (async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/notes`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(noteData)
          });

          if (response.ok) {
            const result = await response.json();
            const newNote = result.note || result;
            GLOBAL_NOTES_CACHE = GLOBAL_NOTES_CACHE.map(n =>
              n.id === tempId ? newNote : n
            );
            if (isMountedRef.current) setNotes([...GLOBAL_NOTES_CACHE]);
          } else {
            GLOBAL_NOTES_CACHE = GLOBAL_NOTES_CACHE.filter(n => n.id !== tempId);
            if (isMountedRef.current) setNotes([...GLOBAL_NOTES_CACHE]);
          }
        } catch (err) {
          GLOBAL_NOTES_CACHE = GLOBAL_NOTES_CACHE.filter(n => n.id !== tempId);
          if (isMountedRef.current) setNotes([...GLOBAL_NOTES_CACHE]);
        }
      })();
    }

    setShowMentalLoadPicker(false);
  }, [token, trackEvent]);

  // ============================================================================
  // FILTER HANDLERS
  // ============================================================================
  const handleClearError = useCallback(() => setError(null), []);
  
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    if (value.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        trackEvent('notes_search', { 
          query_length: value.length,
          has_results: notes.filter(n => 
            n.title?.toLowerCase().includes(value.toLowerCase()) ||
            n.content?.toLowerCase().includes(value.toLowerCase())
          ).length > 0
        });
      }, 500);
    }
  }, [notes, trackEvent]);
  
  const handleCategoryChange = useCallback((e) => {
    const value = e.target.value;
    setFilterCategory(value);
    trackEvent('notes_filter_category', { category: value });
  }, [trackEvent]);

  const handleViewModeChange = useCallback((newMode) => {
    setViewMode(newMode);
    trackEvent('notes_view_mode_change', { from_mode: viewMode, to_mode: newMode });
  }, [viewMode, trackEvent]);

  // ============================================================================
  // SAVE HANDLER
  // ============================================================================
  const handleSaveNote = useCallback(async (noteData) => {
    console.log('💾 handleSaveNote STARTED:', noteData.title);
    if (!token) {
      setError('Kein Token verfügbar');
      return;
    }
    
    const isEdit = !!editingNote;
    
    if (!isEdit) {
      const tempId = `temp-${Date.now()}`;
      const optimisticNote = {
        ...noteData,
        id: tempId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      GLOBAL_NOTES_CACHE = [optimisticNote, ...GLOBAL_NOTES_CACHE];
      GLOBAL_HAS_LOADED = true;
      
      setNotes([...GLOBAL_NOTES_CACHE]);
      setShowModal(false);
      setEditingNote(null);
      
      trackEvent('notes_create', { 
        category: noteData.category,
        title_length: noteData.title?.length || 0,
        content_length: noteData.content?.length || 0
      });
      
      (async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/notes`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(noteData)
          });
          
          if (response.ok) {
            const result = await response.json();
            const newNote = result.note || result;
            GLOBAL_NOTES_CACHE = GLOBAL_NOTES_CACHE.map(n => 
              n.id === tempId ? newNote : n
            );
            if (isMountedRef.current) setNotes([...GLOBAL_NOTES_CACHE]);
          } else {
            throw new Error('Server error');
          }
        } catch (err) {
          trackEvent('notes_create_error', { error: err.message });
          GLOBAL_NOTES_CACHE = GLOBAL_NOTES_CACHE.filter(n => n.id !== tempId);
          if (isMountedRef.current) setNotes([...GLOBAL_NOTES_CACHE]);
        }
      })();
      
    } else {
      const oldNote = editingNote;
      const updatedNote = { ...oldNote, ...noteData, updated_at: new Date().toISOString() };
      
      GLOBAL_NOTES_CACHE = GLOBAL_NOTES_CACHE.map(n => 
        n.id === oldNote.id ? updatedNote : n
      );
      GLOBAL_HAS_LOADED = true;
      
      setNotes([...GLOBAL_NOTES_CACHE]);
      setShowModal(false);
      setEditingNote(null);
      
      trackEvent('notes_edit', { 
        note_id: oldNote.id,
        category: noteData.category,
        title_changed: oldNote.title !== noteData.title,
        content_changed: oldNote.content !== noteData.content
      });
      
      (async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/notes/${oldNote.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(noteData)
          });

          if (response.ok) {
            const result = await response.json();
            const serverNote = result.note || result;
            GLOBAL_NOTES_CACHE = GLOBAL_NOTES_CACHE.map(n =>
              n.id === oldNote.id ? serverNote : n
            );
            if (isMountedRef.current) setNotes([...GLOBAL_NOTES_CACHE]);
          } else {
            throw new Error('Server error');
          }
        } catch (err) {
          trackEvent('notes_edit_error', { error: err.message });
          GLOBAL_NOTES_CACHE = GLOBAL_NOTES_CACHE.map(n => 
            n.id === oldNote.id ? oldNote : n
          );
          if (isMountedRef.current) setNotes([...GLOBAL_NOTES_CACHE]);
        }
      })();
    }
  }, [editingNote, trackEvent, token]);

  // ============================================================================
  // DELETE HANDLER
  // ============================================================================
  const handleDeleteNote = useCallback(async (id) => {
    if (!window.confirm(t('notes.confirmDelete') || 'Notiz wirklich löschen?')) return;
    
    if (!token) {
      setError('Kein Token verfügbar');
      return;
    }
    
    const deletedNote = GLOBAL_NOTES_CACHE.find(n => n.id === id);
    const deletedIndex = GLOBAL_NOTES_CACHE.findIndex(n => n.id === id);
    
    GLOBAL_NOTES_CACHE = GLOBAL_NOTES_CACHE.filter(n => n.id !== id);
    GLOBAL_HAS_LOADED = true;
    
    setNotes([...GLOBAL_NOTES_CACHE]);
    setShowModal(false);
    setEditingNote(null);
    
    trackEvent('notes_delete', { note_id: id, category: deletedNote?.category });
    
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/notes/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) throw new Error('Server error');
      } catch (err) {
        trackEvent('notes_delete_error', { error: err.message });
        if (deletedNote) {
          const newCache = [...GLOBAL_NOTES_CACHE];
          newCache.splice(deletedIndex, 0, deletedNote);
          GLOBAL_NOTES_CACHE = newCache;
          if (isMountedRef.current) setNotes([...GLOBAL_NOTES_CACHE]);
        }
      }
    })();
  }, [t, trackEvent, token]);

  // ============================================================================
  // RENDER
  // ============================================================================
  if (!active) return null;

  // Auth required
  if (!isAuthenticated && !isAuthLoading) {
    return (
      <div className="notes-container">
        <div className="notes-loading">
          <span style={{ fontSize: '3rem' }}>🔒</span>
          <p style={{ margin: 0, fontWeight: 600 }}>{t('common.pleaseLogin')}</p>
        </div>
      </div>
    );
  }

  // Skeleton loading
  if ((isInitialLoad || tokenLoading) && notes.length === 0 && !error) {
    return (
      <div className="notes-container">
        <div className="notes-header-row1">
          <div className="notes-header-left">
            <Skeleton width={44} height={44} borderRadius={12} />
            <div className="notes-header-title-wrap">
              <Skeleton width={100} height={20} style={{ marginBottom: 6 }} />
              <Skeleton width={80} height={14} />
            </div>
          </div>
          <Skeleton width={44} height={44} borderRadius={12} />
        </div>
        <SkeletonGrid count={6} cardHeight={140} columns={2} />
      </div>
    );
  }

  // Auth loading
  if (isAuthLoading) {
    return (
      <div className="notes-container">
        <div className="notes-loading">
          <span style={{ fontSize: '2rem' }}>🔐</span>
          <p>Authentifizierung...</p>
        </div>
      </div>
    );
  }

  // Error – no data
  if (error && notes.length === 0) {
    return (
      <div className="notes-container">
        <div className="notes-error">
          <span style={{ fontSize: '3rem' }}>⚠️</span>
          <p style={{ margin: 0, fontWeight: 600 }}>{error}</p>
          <button onClick={handleRetry}>
            🔄 {t('common.retry') || 'Erneut versuchen'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="notes-container">
      
      {/* Loading Overlay */}
      {showLoading && (
        <LoadingIndicator text="Aktualisiere..." position="fixed" />
      )}
      
      {/* Error Banner (with data) */}
      {error && notes.length > 0 && (
        <div className="notes-error-banner">
          <span>⚠️ {error}</span>
          <button onClick={handleClearError}>✕</button>
        </div>
      )}
      
      <div style={{
        opacity: showLoading ? 0.7 : 1,
        transition: 'opacity 0.2s ease',
        pointerEvents: showLoading ? 'none' : 'auto'
      }}>
        
        {/* ROW 1 - Icon | "Notizen" Title | Info Button */}
        <div className="notes-header-row1">
          <div className="notes-header-left">
            <div className="notes-header-icon">
              📝
              {isLoading && !showLoading && <span className="sync-dot" />}
            </div>
            <div className="notes-header-title-wrap">
              <h1 className="notes-header-title">{t('notes.title') || 'Notizen'}</h1>
              <span className="notes-header-subtitle">{notes.length} Notizen</span>
            </div>
            <button
              className="notes-info-btn"
              onClick={() => setShowInfo(!showInfo)}
              title="Modul-Info"
            >
              ⓘ
            </button>
          </div>
        </div>

        {/* Info Modal */}
        {showInfo && (
          <div className="notes-info-modal">
            <div className="notes-info-modal-header">
              <span className="notes-info-modal-title">Notizen - Info</span>
              <button
                className="notes-info-close-btn"
                onClick={() => setShowInfo(false)}
              >
                ✕
              </button>
            </div>
            <div className="notes-info-modal-content">
              <p className="notes-info-text">
                Das Notizen-Modul ist dein digitaler Notizblock für alle Gedanken, Ideen und wichtigen Informationen.
              </p>
              <ul className="notes-info-list">
                <li>📝 Notizen in verschiedenen Farben erstellen</li>
                <li>📂 Kategorien: Persönlich, Arbeit, Ideen, Wichtig</li>
                <li>🧠 Mental Load Aufgaben als Notizen hinzufügen</li>
                <li>🔍 Volltextsuche über alle Notizen</li>
                <li>▦ Karten-Ansicht oder Listen-Ansicht</li>
                <li>👥 Notizen Haushaltsmitgliedern zuweisen</li>
              </ul>
            </div>
          </div>
        )}

        {/* =====================================================================
            ✅ v31.1: ROW 2 — Ansichtauswahl (Toggle) | Mental Load (Chip)
            =====================================================================
            PLANIVIO BUTTON STRATEGY — Regel 1: Gleiche Höhe in derselben Zeile
            
            PROBLEM (v30):
            - Toggle (36px) und Mental Load Chip hatten unterschiedliche Höhen
            - Mental Load saß visuell höher als der Toggle
            - Ursache 1: .notes-header-actions Wrapper mit margin-left:auto 
              erzeugte eine Extra-Flex-Ebene die vertikale Ausrichtung störte
            - Ursache 2: border (1.5px) wurde NICHT in die 36px eingerechnet
              (fehlende box-sizing: border-box → effektiv 39px statt 36px)
            - Ursache 3: line-height default (~1.5) blähte Buttons von innen auf
            
            LÖSUNG (v31.1):
            - .notes-header-actions Wrapper ENTFERNT — Mental Load Button ist 
              jetzt direktes Flex-Kind von .notes-controls-row
            - marginLeft:'auto' direkt auf den Button (schiebt ihn nach rechts)
            - INLINE STYLES erzwingen height:36px, lineHeight:1, box-sizing 
              auf ALLEN Elementen — überschreibt jedes externe CSS
            - Alle Elemente: exakt 36px = --btn-h-chip aus Button Strategy
            
            NICHT ÄNDERN ohne die PLANIVIO_Button_Strategie.md zu lesen!
            ===================================================================== */}
        <div 
          className="notes-controls-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0 0.75rem 0.5rem',
            gap: '16px',
            flexWrap: 'nowrap',
            height: '36px'           /* Zeilenhöhe = Chip-Höhe, alle Kinder gleich */
          }}
        >
          {/* Toggle-Gruppe: Karten/Liste — btn-toggle-group Pattern (36px) */}
          <div className="view-toggle" style={{ height: '36px', flexShrink: 0 }}>
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => handleViewModeChange('grid')}
              style={{ height: '36px', lineHeight: 1, boxSizing: 'border-box' }}
            >
              ▦ Karten
            </button>
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => handleViewModeChange('list')}
              style={{ height: '36px', lineHeight: 1, boxSizing: 'border-box' }}
            >
              ☰ Liste
            </button>
          </div>

          {/* Mental Load Chip — KEIN Wrapper-Div mehr!
              marginLeft:'auto' schiebt nach rechts, height:36px = gleich wie Toggle
              box-sizing:'border-box' rechnet 1.5px border IN die 36px ein */}
          <button
            className="btn-mental-load"
            onClick={handleOpenMentalLoadPicker}
            style={{
              height: '36px',
              lineHeight: 1,
              boxSizing: 'border-box',
              marginLeft: 'auto',
              flexShrink: 0
            }}
          >
            <span>🧠</span>
            Mental Load
          </button>
        </div>

        {/* =====================================================================
            ✅ v31.2: ROW 3 — Suche | Kategorie-Dropdown
            =====================================================================
            ROOT CAUSE des Abschneidens:
            App.js → styles.appContainer hat overflowX:'hidden' + padding 12px
            + Notes.css → .notes-filters hat padding 0.75rem (12px)
            = Dropdown hat nur 100vw - 48px - gap Platz
            
            FIX: Dropdown als Prozent-Breite (35%) statt feste px-Breite.
            So passt es IMMER, egal wie breit der Screen ist.
            Search bekommt den Rest (65% minus gap).
            ===================================================================== */}
        <div style={{ padding: '0 0.75rem 0.5rem' }}>
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            {/* Search: 65% der Breite */}
            <div className="filter-search-wrap" style={{ flex: '1 1 0', minWidth: 0 }}>
              <span className="search-icon">🔍</span>
              <input
                type="text"
                className="filter-search"
                placeholder={`${t('common.search') || 'Suchen'}...`}
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>

            {/* Dropdown: 35% der Breite, nie kleiner als nötig */}
            <select
              className="filter-select"
              value={filterCategory}
              onChange={handleCategoryChange}
              style={{ 
                width: '35%',
                minWidth: '90px',
                maxWidth: '140px',
                flexShrink: 0,
                boxSizing: 'border-box'
              }}
            >
              <option value="all">Alle</option>
              <option value="personal">Persönlich</option>
              <option value="work">Arbeit</option>
              <option value="ideas">Ideen</option>
              <option value="important">Wichtig</option>
            </select>
          </div>
        </div>

        {/* NOTES CONTENT */}
        <NotesOverview
          notes={notes}
          onEditNote={handleEditNote}
          searchQuery={searchQuery}
          filterCategory={filterCategory}
          viewMode={viewMode}
          setViewMode={handleViewModeChange}
          memberOptions={memberOptions}
          t={t}
          isDark={isDark}
        />
      </div>
    </div>{/* Ende .notes-container */}

      {/* MODALS — auch außerhalb von .notes-container für sauberen z-index */}
      <CreateNoteModal
        show={showModal}
        onClose={handleCloseModal}
        editingNote={editingNote}
        onSave={handleSaveNote}
        onDelete={handleDeleteNote}
        t={t}
        isDark={isDark}
        memberOptions={memberOptions}
      />

      <MentalLoadPicker
        isOpen={showMentalLoadPicker}
        onClose={handleCloseMentalLoadPicker}
        onAddTasks={handleAddMentalLoadTasks}
      />
    </>
  );
};

export default Notes;